<script setup lang="ts">
/**
 * Подъём стенда составом задачи — макет 269:2043.
 *
 * Стенд одного бэкенда бесполезен: смотреть фичу не в чем. Поэтому поднимается
 * не «репозиторий с веткой», а состав задачи целиком — все её сервисы в один
 * namespace. Ветку каждому выбирает сервер: своя, если она в этом репозитории
 * есть, и master, если нет (DEV-1628). Здесь она только показана — чтобы было
 * видно, что именно поедет.
 *
 * Подсказка из веток стоит отдельной строкой без галочки: ветка в чужом
 * репозитории — повод спросить, а не основание молча расширить состав. Её
 * добавляют в состав кнопкой, и это правка задачи, а не разовый выбор.
 */
import { AppButton, AppCheckbox, AppModal, AppTextField } from '@vinprop/design-system'
import { Plus } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { listIssueServices, raiseStand, setIssueServices, type IssueService } from '../api'
import { shortService } from '../format'
import { getDevInfo } from '@/features/issue/devinfo'
import { getIssueByKey } from '@/features/issue/api'

const props = defineProps<{
  /** Задача известна — диалог открыт из её карточки, и спрашивать ключ незачем */
  issueId?: string
  issueKey?: string
}>()

const emit = defineEmits<{ done: []; close: [] }>()

const { t } = useI18n()

const DEFAULT_BRANCH = 'master'

const issueKey = ref(props.issueKey ?? '')
const issueId = ref(props.issueId ?? '')
const services = ref<IssueService[]>([])
const branchOf = ref<Record<string, string>>({})
const picked = ref<string[]>([])
const busy = ref(false)
const isLoading = ref(false)
const error = ref('')

/** Состав — то, что человек задал в карточке; подсказки идут ниже отдельно. */
const declared = computed(() => services.value.filter((service) => !service.suggested))
const suggested = computed(() => services.value.filter((service) => service.suggested))

const stand = computed(() => (issueKey.value || props.issueKey || '').toLowerCase())

const isValid = computed(() => Boolean(issueId.value) && picked.value.length > 0)

/** Ветка сервиса: своя, если она у задачи есть, иначе master — как решит сервер. */
const branchFor = (path: string) => branchOf.value[path] || DEFAULT_BRANCH
const hasOwnBranch = (path: string) => Boolean(branchOf.value[path])

const loadServices = async (id: string) => {
  isLoading.value = true

  try {
    const [list, info] = await Promise.all([listIssueServices(id), getDevInfo(id)])

    services.value = list
    // Свежая ветка репозитория идёт первой — сервис отдаёт их новыми вперёд.
    branchOf.value = info.branches.reduce<Record<string, string>>((map, branch) => {
      if (!map[branch.projectPath]) map[branch.projectPath] = branch.name

      return map
    }, {})
    picked.value = list.filter((service) => !service.suggested).map((service) => service.path)
  } catch {
    services.value = []
    picked.value = []
    error.value = t('devops.envs.raise.servicesFailed')
  } finally {
    isLoading.value = false
  }
}

/** Ключ введён — ищем задачу и показываем её состав. */
const findIssue = async () => {
  const key = issueKey.value.trim().toUpperCase()

  if (!key || key === props.issueKey) return

  error.value = ''

  try {
    const issue = await getIssueByKey(key)

    if (!issue) {
      issueId.value = ''
      services.value = []
      error.value = t('devops.envs.raise.unknownIssue')

      return
    }

    issueId.value = issue.id
    await loadServices(issue.id)
  } catch {
    error.value = t('devops.envs.raise.unknownIssue')
  }
}

watch(issueKey, (value) => {
  if (value.trim().toUpperCase() !== props.issueKey) issueId.value = ''
})

onMounted(() => {
  if (props.issueId) void loadServices(props.issueId)
})

const toggle = (path: string, on: boolean) => {
  picked.value = on ? [...picked.value, path] : picked.value.filter((item) => item !== path)
}

/** Подсказка в состав — это правка задачи: список сервисов задаётся целиком. */
const addSuggested = async (service: IssueService) => {
  busy.value = true

  try {
    await setIssueServices(issueId.value, [
      ...declared.value.map((item) => item.projectId),
      service.projectId,
    ])
    await loadServices(issueId.value)
  } catch {
    error.value = t('devops.envs.raise.servicesFailed')
  } finally {
    busy.value = false
  }
}

const submit = async () => {
  if (!isValid.value) return

  busy.value = true
  error.value = ''

  try {
    await raiseStand(issueId.value, picked.value)
    emit('done')
  } catch {
    error.value = t('devops.envs.actionError')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AppModal
    model-value
    :title="
      issueKey
        ? t('devops.envs.raise.titleFor', { key: issueKey.toUpperCase() })
        : t('devops.envs.raise.title')
    "
    size="small"
    @update:model-value="emit('close')"
  >
    <div class="raise">
      <p v-if="stand" class="raise__hint">
        {{ t('devops.envs.raise.namespace', { stand, name: `vp-f-${stand}` }) }}
      </p>

      <AppTextField
        v-if="!props.issueId"
        v-model="issueKey"
        class="raise__issue"
        :label="t('devops.envs.raise.issue')"
        placeholder="DEV-1421"
        @blur="findIssue"
        @keyup.enter="findIssue"
      />

      <p class="raise__overline">{{ t('devops.envs.raise.composition') }}</p>

      <p v-if="isLoading" class="raise__hint">{{ t('devops.flags.loading') }}</p>
      <p v-else-if="issueId && !services.length" class="raise__hint">
        {{ t('devops.envs.raise.noServices') }}
      </p>
      <p v-else-if="!issueId" class="raise__hint">{{ t('devops.envs.raise.pickIssue') }}</p>

      <!-- Строка сервиса в две строки: сверху имя и откуда ветка, снизу сама
           ветка. В три колонки они разъезжались — модалка узкая, а пути и
           названия веток длинные. -->
      <div v-for="service in declared" :key="service.path" class="raise__row">
        <AppCheckbox
          :model-value="picked.includes(service.path)"
          :label="shortService(service.path)"
          @update:model-value="toggle(service.path, $event)"
        />
        <span class="raise__note">
          {{
            hasOwnBranch(service.path)
              ? t('devops.envs.raise.ownBranch')
              : t('devops.envs.raise.noOwnBranch')
          }}
        </span>
        <span class="raise__branch" :title="branchFor(service.path)">
          {{ branchFor(service.path) }}
        </span>
      </div>

      <!-- Подсказка: ветка задачи в этом репозитории уже есть, а в составе его
           нет. Добавление правит задачу, поэтому оно кнопкой, а не галочкой. -->
      <div v-for="service in suggested" :key="service.path" class="raise__row raise__row--muted">
        <span class="raise__name">{{ shortService(service.path) }}</span>
        <button type="button" class="raise__add" :disabled="busy" @click="addSuggested(service)">
          <Plus :size="13" />
          {{ t('devops.envs.raise.addService') }}
        </button>
        <span class="raise__branch" :title="branchFor(service.path)">
          {{ branchFor(service.path) }}
        </span>
      </div>

      <p v-if="error" class="raise__error">{{ error }}</p>
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" :disabled="!isValid" @click="submit">
        {{ t('devops.stand.raise') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.raise {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.raise__hint,
.raise__error {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}

.raise__error {
  color: var(--color-danger);
}

/* Подпись секции — макет 269:2043: 11px, разрядка, верхний регистр */
.raise__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

/* Сетка вместо ряда: имя слева, пометка справа, ветка отдельной строкой под
   ними — так строка держит любую длину пути и названия ветки. */
.raise__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px 12px;
}

.raise__row--muted .raise__name {
  color: var(--color-text-muted);
}

.raise__name {
  overflow: hidden;
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Ветка — не поле ввода: её выбирает сервер, и подменить выбор здесь нельзя */
.raise__branch {
  grid-column: 1 / -1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.raise__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  white-space: nowrap;
}

.raise__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  cursor: pointer;
}

.raise__add:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}
</style>
